import React, { useState } from "react";
import { Modal, Steps, Button, Typography, Tag, Tooltip, Alert } from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  LinkOutlined,
  RobotOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import "./TelegramSetupGuideModal.scss";

interface TelegramSetupGuideModalProps {
  open: boolean;
  onClose: () => void;
  onOpenAddBot: () => void;
}

export const TelegramSetupGuideModal: React.FC<TelegramSetupGuideModalProps> = ({
  open,
  onClose,
  onOpenAddBot,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => {
      setCopiedText(null);
    }, 2000);
  };

  const steps = [
    {
      title: "Create Bot & Token",
      icon: <RobotOutlined />,
    },
    {
      title: "Get Chat ID",
      icon: <MessageOutlined />,
    },
    {
      title: "Report Categories",
      icon: <SettingOutlined />,
    },
    {
      title: "Complete Setup",
      icon: <CheckCircleOutlined />,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={740}
      className="rs-modal telegram-guide-modal"
      destroyOnClose
    >
      <div className="telegram-guide-header">
        <div className="guide-title-badge">
          <RobotOutlined /> Telegram Bot Setup Guide
        </div>
        <h2>4-Step Guide to Connect Telegram Bot to CMD Client</h2>
        <p className="guide-subtitle">
          Follow this step-by-step guide from creating a bot on Telegram to receiving automated reports.
        </p>
      </div>

      <Steps
        current={currentStep}
        onChange={(step) => setCurrentStep(step)}
        items={steps}
        className="telegram-guide-steps"
      />

      <div className="telegram-guide-content">
        {/* STEP 1: CREATE BOT & GET TOKEN */}
        {currentStep === 0 && (
          <div className="guide-step-panel">
            <h3 className="step-heading">
              <span className="step-num">1</span> Create a New Telegram Bot & Get API Token
            </h3>

            <div className="guide-instruction-card">
              <div className="instruction-item">
                <span className="bullet">1</span>
                <div>
                  <strong>Open Telegram and search for @BotFather:</strong>
                  <p>
                    BotFather is Telegram's official bot used to create and manage all other bots.
                  </p>
                  <a
                    href="https://t.me/BotFather"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="telegram-link-btn"
                  >
                    <LinkOutlined /> Open @BotFather on Telegram
                  </a>
                </div>
              </div>

              <div className="instruction-item">
                <span className="bullet">2</span>
                <div>
                  <strong>Send command to create a new bot:</strong>
                  <p>In your chat with @BotFather, send the command:</p>
                  <div className="code-copy-box">
                    <code>/newbot</code>
                    <Tooltip title={copiedText === "/newbot" ? "Copied!" : "Copy command"}>
                      <Button
                        size="small"
                        icon={copiedText === "/newbot" ? <CheckOutlined /> : <CopyOutlined />}
                        onClick={() => handleCopy("/newbot", "/newbot")}
                        className="copy-btn"
                      >
                        {copiedText === "/newbot" ? "Copied" : "Copy"}
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div className="instruction-item">
                <span className="bullet">3</span>
                <div>
                  <strong>Enter Display Name & Username for your Bot:</strong>
                  <ul>
                    <li>
                      <strong>Display Name:</strong> Enter any name (e.g., <code>CMD Notification Bot</code>).
                    </li>
                    <li>
                      <strong>Username:</strong> Must end with <code>bot</code> or <code>_bot</code> (e.g., <code>cmd_tech_notify_bot</code>).
                    </li>
                  </ul>
                </div>
              </div>

              <div className="instruction-item highlight-item">
                <span className="bullet">4</span>
                <div>
                  <strong>Save your HTTP API Token:</strong>
                  <p>After successful creation, BotFather will reply with a message containing your Token:</p>
                  <div className="token-example-box">
                    <code>7123456789:AAFgX_example_token_abcdef123456</code>
                  </div>
                  <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message="Use this token for the 'Bot Token / API Key' field when adding a Bot to CMD Client."
                    className="step-alert"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: GET CHAT ID */}
        {currentStep === 1 && (
          <div className="guide-step-panel">
            <h3 className="step-heading">
              <span className="step-num">2</span> Get Telegram Chat ID (Personal or Group/Channel)
            </h3>

            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="Important: You must press START with your Bot first before it has permission to send messages to you!"
              className="step-alert-top"
            />

            <div className="chat-id-options">
              {/* Option A: Personal Chat */}
              <div className="option-card">
                <h4><MessageOutlined /> Option 1: Personal Messages (1:1)</h4>
                <ol>
                  <li>
                    Open a chat with <strong>your newly created Bot</strong> and press <strong>START</strong> (or send <code>/start</code>).
                  </li>
                  <li>
                    Open user ID lookup bot <strong>@userinfobot</strong>:
                    <div style={{ marginTop: 6 }}>
                      <a
                        href="https://t.me/userinfobot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="telegram-link-btn"
                      >
                        <LinkOutlined /> Open @userinfobot
                      </a>
                    </div>
                  </li>
                  <li>
                    Send any message to <code>@userinfobot</code>, and it will return your ID (e.g., <code className="chat-id-demo">123456789</code>).
                  </li>
                </ol>
              </div>

              {/* Option B: Group or Channel */}
              <div className="option-card">
                <h4><RobotOutlined /> Option 2: Messages in Group / Channel</h4>
                <ol>
                  <li>Add your Bot to the Telegram Group/Channel.</li>
                  <li>Grant <strong>Administrator</strong> permissions to your Bot in the Group/Channel.</li>
                  <li>
                    Add <strong>@GetIDBot</strong> or <strong>@RawDataBot</strong> to the group to get the group ID:
                    <div style={{ marginTop: 6 }}>
                      <a
                        href="https://t.me/GetIDBot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="telegram-link-btn"
                      >
                        <LinkOutlined /> Open @GetIDBot
                      </a>
                    </div>
                  </li>
                  <li>
                    Group/Channel Chat IDs usually start with a minus <code>-</code> or <code>-100</code> (e.g., <code className="chat-id-demo">-1001987654321</code>).
                  </li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: REPORT CATEGORIES */}
        {currentStep === 2 && (
          <div className="guide-step-panel">
            <h3 className="step-heading">
              <span className="step-num">3</span> Select Report Categories for Bot
            </h3>

            <p className="step-desc">
              CMD Client allows filtering report types sent via Telegram. You can select one or multiple report types based on your needs:
            </p>

            <div className="report-types-grid">
              <div className="report-card">
                <div className="report-card-icon machine-icon">🖥️</div>
                <div className="report-card-body">
                  <Tag color="cyan">Machine</Tag>
                  <h4>Machine / Equipment Reports</h4>
                  <p>Machine status updates, technical fault alerts, and automatic downtime warnings.</p>
                </div>
              </div>

              <div className="report-card">
                <div className="report-card-icon staff-icon">👥</div>
                <div className="report-card-body">
                  <Tag color="gold">Staff</Tag>
                  <h4>Staff / Operations Reports</h4>
                  <p>Shift notifications, attendance tracking, task progress, and assignment statuses.</p>
                </div>
              </div>

              <div className="report-card">
                <div className="report-card-icon manager-icon">📊</div>
                <div className="report-card-body">
                  <Tag color="purple">Manager</Tag>
                  <h4>Manager / Executive Reports</h4>
                  <p>Daily performance reports, KPI summaries, and key operational insights for management.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: COMPLETE & TEST */}
        {currentStep === 3 && (
          <div className="guide-step-panel">
            <h3 className="step-heading">
              <span className="step-num">4</span> Fill Form & Activate Bot
            </h3>

            <div className="guide-summary-card">
              <h4>Pre-submission Checklist:</h4>
              <div className="checklist-items">
                <div className="check-item">
                  <CheckCircleOutlined className="check-icon" />
                  <span>Obtained <strong>Bot Token</strong> from @BotFather.</span>
                </div>
                <div className="check-item">
                  <CheckCircleOutlined className="check-icon" />
                  <span>Obtained <strong>Default Chat ID</strong> (e.g., <code>123456789</code> or <code>-100...</code>).</span>
                </div>
                <div className="check-item">
                  <CheckCircleOutlined className="check-icon" />
                  <span>Pressed <strong>START</strong> to initiate chat with Bot on Telegram.</span>
                </div>
                <div className="check-item">
                  <CheckCircleOutlined className="check-icon" />
                  <span>Selected relevant report categories (Machine / Staff / Manager).</span>
                </div>
              </div>

              <div className="complete-action-box">
                <p>You are ready to add the Bot to the system!</p>
                <Button
                  type="primary"
                  size="large"
                  icon={<ArrowRightOutlined />}
                  className="start-add-bot-btn"
                  onClick={() => {
                    onClose();
                    onOpenAddBot();
                  }}
                >
                  Add Bot Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FOOTER ACTIONS */}
      <div className="telegram-guide-footer">
        <Button
          disabled={currentStep === 0}
          onClick={() => setCurrentStep((prev) => prev - 1)}
          className="rs-button guide-prev-btn"
        >
          Previous
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button
            type="primary"
            onClick={() => setCurrentStep((prev) => prev + 1)}
            className="rs-button guide-next-btn"
          >
            Next <ArrowRightOutlined />
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => {
              onClose();
              onOpenAddBot();
            }}
            className="rs-button guide-finish-btn"
          >
            Add New Bot
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default TelegramSetupGuideModal;
